import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useSelector } from "react-redux";

import { checkDineInExpiry } from "./utils/checkDineInExpiry";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Topbar from "./components/Topbar";
import Menu from "./Menu";
import LoginModal from "./components/LoginModal";
import RegisterModal from "./components/RegisterModal";
import ForgotPasswordModal from "./components/ForgotPasswordModal";
import SeoManager from "./components/SeoManager";
import ThankYouPage from "./pages/ThankYouPage";
import { SettingsProvider } from "./context/SettingsContext";
import DineInRoute from "./pages/DineInRoute";
import OrderList from "./pages/OrderList";
import ContactUs from "./pages/ContactUs";
import AboutUs from "./pages/AboutUs";
import GiftVoucher from "./pages/GiftVoucher";
import ThankYouGift from "./pages/ThankYouGift";
import MyAccount from "./pages/MyAccount";
import ReservationForm from "./pages/ReservationForm";
import AppInRoute from "./pages/AppInRoute";

import { ModalContext } from "./context/ModalContext";

// ⭐ GLOBAL MODALS
import {
  GlobalModalProvider,
  GlobalModalContext,
} from "./context/ModalProvider";
import CustomPopupModal from "./components/CustomPopupModal";

/* -------------------------------------------------------------------------- */
/*                        GLOBAL PAGE MODAL MANAGER                            */
/* -------------------------------------------------------------------------- */
function ModalManager() {
  const { pages, activeModal, evaluatePageModal, closeModal } =
    React.useContext(GlobalModalContext);

  const location = useLocation();

  useEffect(() => {
    if (!pages || pages.length === 0) return;

    const currentPath = location.pathname.toLowerCase();

    const currentPage = pages.find((p) => {
      const urlPath =
        "/" + p.page_name.replace(/\s+/g, "-").toLowerCase();
      return urlPath === currentPath;
    });

    if (currentPath === "/") {
      evaluatePageModal(5);
    } else if (currentPage) {
      evaluatePageModal(currentPage.id);
    }
  }, [location.pathname, pages]);

  return <CustomPopupModal modal={activeModal} onClose={closeModal} />;
}

/* -------------------------------------------------------------------------- */
/*           URL QUERY AUTH MODAL HANDLER (AUTO REMOVE PARAM)                  */
/* -------------------------------------------------------------------------- */
function QueryModalHandler({
  openLogin,
  openRegister,
  closeAllModals,
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const modal = searchParams.get("modal");

    if (!modal) return;

    // ✅ Already logged in → clean URL & exit
    if (user) {
      closeAllModals();
      navigate(".", { replace: true });
      return;
    }

    if (modal === "login") {
      openLogin();
    } else if (modal === "register") {
      openRegister();
    }

    // 🧹 Remove modal param from URL
    navigate(".", { replace: true });
  }, [searchParams, user]);

  return null;
}

/* -------------------------------------------------------------------------- */
/*                                   APP                                      */
/* -------------------------------------------------------------------------- */
function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  /* ---------------------------- DINE-IN EXPIRY ----------------------------- */
  useEffect(() => {
    checkDineInExpiry();
  }, []);

  /* ------------------------ FORGOT PASSWORD EVENT ------------------------- */
  useEffect(() => {
    const openForgot = () => setShowForgotPassword(true);
    window.addEventListener("openForgotPassword", openForgot);
    return () =>
      window.removeEventListener("openForgotPassword", openForgot);
  }, []);

  const basename =
    window.location.hostname === "localhost" ? "/" : "/menu-app";

  return (
    <SettingsProvider>
      <GlobalModalProvider>
        <ModalContext.Provider
          value={{
            openLogin: () => setShowLogin(true),
            openRegister: () => setShowRegister(true),
          }}
        >
          <Router basename={basename}>
            <SeoManager />

            {/* ✅ URL AUTH MODAL HANDLER */}
            <QueryModalHandler
              openLogin={() => setShowLogin(true)}
              openRegister={() => setShowRegister(true)}
              closeAllModals={() => {
                setShowLogin(false);
                setShowRegister(false);
                setShowForgotPassword(false);
              }}
            />

            {/* ✅ CMS MODALS */}
            <ModalManager />

            <Topbar
              onLogin={() => setShowLogin(true)}
              onRegister={() => setShowRegister(true)}
            />

            <Routes>
              <Route path="/" element={<Menu />} />
              <Route path="/orders" element={<OrderList />} />
              <Route path="/appdevice" element={<AppInRoute />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/dinein" element={<DineInRoute />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/gift-voucher" element={<GiftVoucher />} />
              <Route path="/my-account" element={<MyAccount />} />
              <Route path="/thank-you-gift" element={<ThankYouGift />} />
              <Route path="/reservation" element={<ReservationForm />} />
            </Routes>

            {/* ---------------------------- AUTH MODALS ---------------------------- */}
            {showLogin && (
              <LoginModal onClose={() => setShowLogin(false)} />
            )}
            {showRegister && (
              <RegisterModal onClose={() => setShowRegister(false)} />
            )}
            {showForgotPassword && (
              <ForgotPasswordModal
                onClose={() => setShowForgotPassword(false)}
              />
            )}

            <ToastContainer />
          </Router>
        </ModalContext.Provider>
      </GlobalModalProvider>
    </SettingsProvider>
  );
}

export default App;
